const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'libs/common_web/src/modules/CIS4m/components/CIS4mDatePicker.tsx';
import { forwardRef } from 'react';
import moment, { Moment } from 'moment';
import { cx } from '@emotion/css';
import { useTheme } from '@mui/material/styles';
import { TextFieldProps } from '@mui/material';
import { useCIS4mValidation } from '../hooks';
import { CIS4mValueComponentProps } from '../types';
import { DatePicker } from '@mui/x-date-pickers';
import { dateActions, useDesktopModeMediaQuery } from '../../../components/Pickers/pickerDefaults';
import { KISIM_DATE_FORMAT, KISIM_STD_DATE_FORMAT } from '../../../defs/generalDefs';
import { useCSSClasses } from '../styles';
import useBreakpoint from '../../../hooks/useBreakpoint';
import { TextInput, useText } from '../../../utils/intlUtils';
import { useCIS4mContext } from '../context';

interface Props extends CIS4mValueComponentProps {
    className?: string;
    label: TextInput;
    required?: boolean;
    checkValidity?: () => string;
    format?: string;
    minDate?: Moment;
    maxDate?: Moment;
}

const sxTextField: TextFieldProps['sx'] = {
    mt: 1,
    mb: 1,
};

export const CIS4mDatePicker = forwardRef((props: Props, ref: any) => {
    const { variant } = useCIS4mContext().style;
    const {
        checkValidity,
        className,
        disabled = false,
        hidden = false,
        initialValue,
        path,
        readonly = false,
        required = false,
    } = props;
    const format = props.format ?? KISIM_DATE_FORMAT;

    const label = useText(props.label);

    const theme = useTheme();
    const desktopModeMediaQuery = useDesktopModeMediaQuery();
    const isLargeDevice = useBreakpoint(theme?.breakpoints?.values?.lg);
    const commonClasses = useCSSClasses();

    const [value, setValue, errorMessage] = useCIS4mValidation(ref, {
        checkValidity,
        disabled,
        hidden,
        initialValue,
        path,
        required,
    });

    return hidden ? null : (
        <div className={cx(commonClasses.cssFieldHorizontal, className)}>
            <DatePicker
                label={label}
                minDate={props.minDate}
                maxDate={props.maxDate}
                value={moment(value, format)}
                desktopModeMediaQuery={desktopModeMediaQuery}
                onChange={(newValue: Moment) => {
                    if (!!newValue) {
                        setValue(newValue.format(format));
                    } else {
                        setValue('');
                    }
                }}
                format={KISIM_STD_DATE_FORMAT}
                showDaysOutsideCurrentMonth={true}
                orientation={isLargeDevice ? 'portrait' : 'landscape'}
                slotProps={{
                    actionBar: ({ pickerVariant }) => ({ actions: dateActions(pickerVariant, { required }) }),
                    textField: {
                        fullWidth: true,
                        slotProps: { input: { readOnly: readonly } },
                        size: 'small',
                        color: !!errorMessage ? 'error' : 'primary',
                        required: required,
                        disabled: disabled,
                        hidden: hidden,
                        error: !!errorMessage,
                        helperText: errorMessage,
                        sx: sxTextField,
                        variant: variant,
                    },
                }}
                localeText={{
                    toolbarTitle: label as string,
                }}
            />
        </div>
    );
});

CIS4mDatePicker.displayName = 'CIS4mDatePicker';
